import { DateTime } from 'luxon';

/**
 * Renders a relative date (e.g. "3 days ago") inside a semantic <time> element whose
 * `dateTime` attribute and `title` carry the exact, absolute timestamp — so hovering
 * reveals the precise moment the relative phrase refers to.
 *
 * Accepts a Date, an ISO string, millis, or null/undefined; renders `fallback` (default "—")
 * when the value is missing or unparseable.
 */
export const RelativeTime = ({
	className,
	fallback = '—',
	value,
}: {
	className?: string;
	fallback?: string;
	value: Date | string | number | null | undefined;
}) => {
	if (value === null || value === undefined || value === '') return <>{fallback}</>;

	const dt =
		value instanceof Date
			? DateTime.fromJSDate(value)
			: typeof value === 'number'
				? DateTime.fromMillis(value)
				: DateTime.fromISO(value).isValid
					? DateTime.fromISO(value)
					: DateTime.fromJSDate(new Date(value));

	if (!dt.isValid) return <>{fallback}</>;

	const relative = dt.toRelative();
	if (!relative) return <>{fallback}</>;

	return (
		<time className={className} dateTime={dt.toISO() ?? undefined} title={dt.toLocaleString(DateTime.DATETIME_FULL)}>
			{relative}
		</time>
	);
};
